import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { usePermissionCheck } from '../components/PermissionGate';
import { isClientRole } from '../auth/roleUtils';
import { isPublicStandalonePath } from '../utils/publicStandaloneRoutes';
import { isStaffClinicalPath } from '../utils/openLaborStaffRoutes';
import openLaborEpisodesApi from '../utils/api/openLaborEpisodesApi';
import { organizationKeys } from '../utils/queryClient';

export const openLaborEpisodesQueryKey = organizationKeys.openLaborEpisodes();

const EMPTY_RESULT = Object.freeze({ items: [], hasMore: false });

async function fetchOpenLaborEpisodes() {
  try {
    return await openLaborEpisodesApi.list();
  } catch (error) {
    if (error?.status === 404) {
      return EMPTY_RESULT;
    }
    throw error;
  }
}

export function useOpenLaborEpisodesQueryOptions({ enabled }) {
  return {
    queryKey: openLaborEpisodesQueryKey,
    queryFn: fetchOpenLaborEpisodes,
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    throwOnError: false,
  };
}

export default function useOpenLaborEpisodes() {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const { can, loading: permissionsLoading } = usePermissionCheck();
  const role = typeof localStorage !== 'undefined' ? localStorage.getItem('userRole') : null;

  const enabled = Boolean(
    isAuthenticated
    && !isPublicStandalonePath(pathname)
    && !isClientRole(role)
    && isStaffClinicalPath(pathname)
    && !permissionsLoading
    && can('ViewClients')
  );

  const query = useQuery(useOpenLaborEpisodesQueryOptions({ enabled }));
  // Disabled queries retain cached data; never expose staff labors on another surface.
  return enabled ? query : { ...query, data: EMPTY_RESULT };
}
